// Shared 2026 renovation-loan rate engine. Used by both:
//   1. <LoanComparisonCalculator> on /guides/how-to-finance-a-home-renovation-2026
//   2. <RateSnapshotWidget> on the homepage hero
//
// Keeping a single source of truth means any rate-card update (every 6 months
// when we refresh from Bankrate / Federal Reserve H.15) only edits one file.
//
// Rate cards reflect Feb 2026 averages across major US lenders. State and
// credit-score adjustments are documented in
// /guides/best-states-for-home-renovation-financing-2026.

export const RATES_2026 = {
  heloc: { base: 8.4 },           // 2026 national avg HELOC, prime + spread
  home_equity: { base: 8.4 },     // 2nd-mortgage fixed
  cash_out_refi: { base: 7.1 },   // first-lien refinance
  personal: { base: 14.8 },       // unsecured 5–7yr
};

// State-level basis-point adjustment off the national avg.
// Negative = cheaper than national avg (better state for financing).
export const STATE_ADJUST = {
  AL: 80, AK: 70, AZ: 0, AR: 60, CA: -60, CO: -10, CT: -60, DE: 0, FL: 10,
  GA: 20, HI: -40, ID: 70, IL: 0, IN: 50, IA: 30, KS: 50, KY: 60, LA: 70,
  ME: -10, MD: -30, MA: -50, MI: 30, MN: -40, MS: 100, MO: 20, MT: 60, NE: 30,
  NV: 20, NH: -30, NJ: -40, NM: 20, NY: -50, NC: 10, ND: 40, OH: 20, OK: 40,
  OR: -20, PA: 0, RI: -40, SC: 20, SD: 50, TN: 50, TX: 10, UT: -10, VT: -20,
  VA: 0, WA: -30, WV: 90, WI: 10, WY: 70, DC: -20,
};

export const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["DC","D.C."],["FL","Florida"],
  ["GA","Georgia"],["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],
  ["IA","Iowa"],["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],
  ["MD","Maryland"],["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],
  ["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],
  ["NJ","New Jersey"],["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],
  ["OH","Ohio"],["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],
  ["SC","South Carolina"],["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],
  ["VT","Vermont"],["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];

export const STATE_NAME_BY_CODE = Object.fromEntries(US_STATES);

// Credit-score → rate adjustment (basis points). Same adjustment applied to
// all secured products; personal loans are roughly 4× more credit-sensitive.
export function creditAdjustSecured(score) {
  if (score >= 760) return -60;
  if (score >= 720) return -20;
  if (score >= 680) return 30;
  if (score >= 640) return 100;
  return 200;
}
export function creditAdjustPersonal(score) {
  if (score >= 760) return -200;
  if (score >= 720) return -80;
  if (score >= 680) return 60;
  if (score >= 640) return 240;
  return 400;
}

export function effectiveRate(product, state, score) {
  const card = RATES_2026[product];
  if (!card) return 0;
  const stateBp = STATE_ADJUST[state] ?? 0;
  const creditBp =
    product === "personal" ? creditAdjustPersonal(score) : creditAdjustSecured(score);
  return card.base + stateBp / 100 + creditBp / 100;
}

export function monthlyPayment(principal, aprPct, months) {
  if (principal <= 0 || months <= 0) return 0;
  const r = aprPct / 100 / 12;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
}

export function formatUSD(n) {
  if (!isFinite(n)) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

// Build the canonical 4-product comparison sorted cheapest-first. Used by
// both the full calculator and the homepage widget.
export function compareProducts({ amount, state, score, months }) {
  const items = [
    { key: "cash_out_refi", label: "Cash-Out Refinance", sub: "Replaces existing mortgage", rate: effectiveRate("cash_out_refi", state, score) },
    { key: "heloc",         label: "HELOC",              sub: "Variable rate, 2nd lien",   rate: effectiveRate("heloc", state, score) },
    { key: "home_equity",   label: "Home Equity Loan",   sub: "Fixed rate, 2nd lien",      rate: effectiveRate("home_equity", state, score) },
    { key: "personal",      label: "Personal Loan",      sub: "Unsecured",                 rate: effectiveRate("personal", state, score) },
  ].map((p) => {
    const m = monthlyPayment(amount, p.rate, months);
    return { ...p, monthly: m, total: m * months, interest: m * months - amount };
  });
  return items.sort((a, b) => a.total - b.total);
}
